<template>
  <div class="route-making-container" dir="rtl">
    <vue-element-loading :active="isLoading" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== PAGE HEADER ==================== -->
    <div class="rm-header-row mb-6">
      <div class="rm-header-titles">
        <div class="d-flex align-center gap-2 mb-1 flex-wrap">
          <span class="rm-badge-section">مجموعات الرحلات</span>
          <span class="caption grey--text text--darken-1 font-weight-medium">تخطيط وجدولة الرحلات</span>
        </div>
        <h1 class="rm-main-title">إدارة مجموعات الرحلات</h1>
        <p class="rm-subtitle">تجميع وإدارة الرحلات التشغيلية ومتابعة تكليفات التشغيل والمسارات المرتبطة</p>
      </div>

      <div class="rm-header-actions d-flex align-center gap-2">
        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="exportGroupsCsv"
          :disabled="groupsList.length === 0"
        >
          <v-icon right size="18" class="ms-1">mdi-download</v-icon>
          تصدير CSV
        </v-btn>

        <v-btn
          depressed
          class="rm-btn-primary"
          to="/trip-groups/create"
        >
          <v-icon right size="18" class="ms-1">mdi-plus</v-icon>
          إضافة مجموعة جديدة
        </v-btn>
      </div>
    </div>

    <!-- ==================== KPI SUMMARY CARDS ==================== -->
    <div class="rm-cards-grid mb-6">
      <!-- 1. Total Groups -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">إجمالي المجموعات</span>
          <div class="rm-stat-val-wrapper">
            <span class="rm-stat-value">{{ kpis.total }}</span>
          </div>
          <span class="rm-stat-caption">جميع مجموعات الرحلات المسجلة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="26" color="#082C69">mdi-folder-multiple-outline</v-icon>
        </div>
      </div>

      <!-- 2. Published Groups -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المجموعات المنشورة</span>
          <div class="rm-stat-val-wrapper">
            <span class="rm-stat-value text-emerald">{{ kpis.published }}</span>
          </div>
          <span class="rm-stat-caption">مجموعات جاهزة للتشغيل الفعلي</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-green">
          <v-icon size="26" color="#10B981">mdi-check-circle-outline</v-icon>
        </div>
      </div>

      <!-- 3. Draft Groups -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المجموعات المسودة</span>
          <div class="rm-stat-val-wrapper">
            <span class="rm-stat-value text-amber">{{ kpis.draft }}</span>
          </div>
          <span class="rm-stat-caption">مجموعات قيد الإعداد والمراجعة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-amber">
          <v-icon size="26" color="#F59E0B">mdi-clock-outline</v-icon>
        </div>
      </div>

      <!-- 4. Inactive Groups -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المجموعات غير النشطة</span>
          <div class="rm-stat-val-wrapper">
            <span class="rm-stat-value text-rose">{{ kpis.inactive }}</span>
          </div>
          <span class="rm-stat-caption">مجموعات معطلة أو خارج الخدمة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-rose">
          <v-icon size="26" color="#EF4444">mdi-close-circle-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== FILTER BAR ==================== -->
    <div class="rm-card mb-6">
      <div class="rm-filters-wrapper">
        <v-row dense class="align-center">
          <!-- Search -->
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              placeholder="ابحث بكود المجموعة أو الاسم..."
              outlined
              dense
              hide-details
              clearable
              class="rm-filter-input"
              @input="onFilterChange"
            ></v-text-field>
          </v-col>

          <!-- Status Filter -->
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              item-text="label"
              item-value="value"
              placeholder="كل الحالات"
              outlined
              dense
              hide-details
              class="rm-filter-input"
              @change="onFilterChange"
            ></v-select>
          </v-col>

          <!-- Route Filter -->
          <v-col cols="12" sm="6" md="3">
            <v-autocomplete
              v-model="filters.route_id"
              :items="routesList"
              item-text="displayName"
              item-value="id"
              placeholder="كل المسارات"
              outlined
              dense
              hide-details
              clearable
              class="rm-filter-input"
              @change="onFilterChange"
            ></v-autocomplete>
          </v-col>

          <!-- Responsible Filter (Role 4 Operations) -->
          <v-col cols="12" sm="6" md="3">
            <v-autocomplete
              v-model="filters.operation_user_id"
              :items="operationFilterOptions"
              item-text="label"
              item-value="value"
              placeholder="مسؤول التشغيل: الكل"
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-account-tie-outline"
              class="rm-filter-input"
              @change="onFilterChange"
            ></v-autocomplete>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- ==================== GROUPS TABLE ==================== -->
    <div class="rm-card">
      <div class="rm-card-header d-flex align-center justify-space-between mb-4 flex-wrap gap-2">
        <div class="d-flex align-center">
          <div class="rm-icon-header-box me-3">
            <v-icon size="20" color="#082C69">mdi-format-list-bulleted</v-icon>
          </div>
          <div>
            <h2 class="rm-card-title">قائمة مجموعات الرحلات</h2>
            <p class="rm-card-subtitle">مراجعة المجموعات النشطة والمسودات وتفاصيل التكليفات</p>
          </div>
        </div>

        <div class="d-flex align-center gap-2">
          <span class="caption text-muted">عدد العناصر في الصفحة:</span>
          <v-select
            v-model="pagination.per_page"
            :items="[10, 25, 50]"
            dense
            outlined
            hide-details
            class="rm-per-page-select"
            @change="fetchGroups"
          ></v-select>
        </div>
      </div>

      <!-- Table Content -->
      <div class="table-responsive">
        <table class="rm-custom-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 50px;">#</th>
              <th>كود المجموعة</th>
              <th>اسم المجموعة</th>
              <th>ملخص المسارات</th>
              <th>مسؤول التشغيل</th>
              <th class="text-center">عدد الرحلات</th>
              <th class="text-center">إجمالي المسافة</th>
              <th class="text-center">الحالة</th>
              <th>تاريخ الإنشاء</th>
              <th class="text-center" style="width: 140px;">الإجراءات</th>
            </tr>
          </thead>
          <tbody v-if="groupsList.length > 0">
            <tr v-for="(group, idx) in groupsList" :key="group.id">
              <td class="text-center font-weight-medium text-muted">
                {{ (pagination.current_page - 1) * pagination.per_page + idx + 1 }}
              </td>

              <!-- Group Code -->
              <td>
                <span class="rm-code-pill font-weight-bold">{{ group.group_code }}</span>
              </td>

              <!-- Group Name -->
              <td>
                <router-link :to="`/trip-groups/${group.id}`" class="rm-group-name-link">
                  {{ group.name }}
                </router-link>
              </td>

              <!-- Routes Summary -->
              <td>
                <v-tooltip bottom v-if="group.routes_summary && group.routes_summary.all_routes && group.routes_summary.all_routes.length > 1">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" class="rm-routes-pill">
                      <v-icon x-small color="#0284C7" class="me-1">mdi-routes</v-icon>
                      {{ group.routes_summary.display_text }}
                    </span>
                  </template>
                  <div>
                    <div v-for="r in group.routes_summary.all_routes" :key="r.id" class="caption py-1">
                      {{ r.code }} - {{ r.name }} ({{ r.count }} رحلة)
                    </div>
                  </div>
                </v-tooltip>
                <span v-else class="rm-single-route text-truncate d-inline-block" style="max-width: 180px;">
                  {{ group.routes_summary ? group.routes_summary.display_text : 'لا توجد مسارات' }}
                </span>
              </td>

              <!-- Operation User -->
              <td>
                <div v-if="group.current_operation_user" class="d-flex align-center">
                  <v-avatar size="24" color="#082C69" class="white--text me-2 caption font-weight-bold">
                    {{ group.current_operation_user.name.charAt(0) }}
                  </v-avatar>
                  <span class="caption font-weight-medium text-navy">{{ group.current_operation_user.name }}</span>
                </div>
                <span v-else class="rm-unassigned-pill">غير معيّن</span>
              </td>

              <!-- Trips Count -->
              <td class="text-center font-weight-bold">
                <span class="rm-count-badge">{{ group.trips_count }}</span>
              </td>

              <!-- Total Distance -->
              <td class="text-center font-weight-medium">
                {{ group.total_distance_km }} كم
              </td>

              <!-- Status -->
              <td class="text-center">
                <span
                  class="rm-status-badge"
                  :class="{
                    'status-published': group.status === 'published',
                    'status-draft': group.status === 'draft',
                    'status-inactive': group.status === 'inactive'
                  }"
                >
                  <span class="status-dot"></span>
                  {{ group.status_label }}
                </span>
              </td>

              <!-- Created Date -->
              <td class="caption text-muted">
                {{ group.created_at || '—' }}
              </td>

              <!-- Actions -->
              <td class="text-center">
                <div class="d-flex align-center justify-center gap-1">
                  <!-- View Details -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        :to="`/trip-groups/${group.id}`"
                        class="rm-action-btn btn-view"
                      >
                        <v-icon small color="#082C69">mdi-eye-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>عرض التفاصيل</span>
                  </v-tooltip>

                  <!-- Edit Group -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        :to="`/trip-groups/${group.id}/edit`"
                        class="rm-action-btn btn-edit"
                      >
                        <v-icon small color="#0284C7">mdi-pencil-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>تعديل المجموعة</span>
                  </v-tooltip>

                  <!-- More menu for activate/deactivate -->
                  <v-menu offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        class="rm-action-btn"
                      >
                        <v-icon small color="#64748B">mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense class="py-0">
                      <v-list-item
                        v-if="group.status !== 'published'"
                        @click="changeGroupStatus(group, 'published')"
                        :disabled="group.trips_count === 0"
                      >
                        <v-list-item-icon class="me-2 my-1">
                          <v-icon small color="#10B981">mdi-check-circle-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>نشر المجموعة</v-list-item-title>
                      </v-list-item>

                      <v-list-item
                        v-if="group.status === 'published'"
                        @click="changeGroupStatus(group, 'inactive')"
                      >
                        <v-list-item-icon class="me-2 my-1">
                          <v-icon small color="#EF4444">mdi-cancel</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>تعطيل المجموعة</v-list-item-title>
                      </v-list-item>

                      <v-list-item
                        v-if="group.status === 'inactive'"
                        @click="changeGroupStatus(group, 'draft')"
                      >
                        <v-list-item-icon class="me-2 my-1">
                          <v-icon small color="#F59E0B">mdi-restore</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>إعادة لمسودة</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </td>
            </tr>
          </tbody>

          <!-- Empty State -->
          <tbody v-else-if="!isLoading">
            <tr>
              <td colspan="10" class="text-center py-12">
                <div class="d-flex flex-column align-center justify-center">
                  <div class="rm-empty-icon-box mb-3">
                    <v-icon size="40" color="#94A3B8">mdi-folder-open-outline</v-icon>
                  </div>
                  <h3 class="font-weight-bold text-navy mb-1">لا توجد مجموعات رحلات مطابقة</h3>
                  <p class="caption text-muted mb-4">لم يتم العثور على مجموعات رحلات وفق معايير البحث المحددة</p>
                  <v-btn
                    depressed
                    class="rm-btn-primary"
                    to="/trip-groups/create"
                  >
                    <v-icon right size="18" class="ms-1">mdi-plus</v-icon>
                    إنشاء أول مجموعة رحلات
                  </v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.total_pages > 1" class="d-flex align-center justify-space-between mt-4 flex-wrap gap-2 pt-3 border-top">
        <span class="caption text-muted">
          عرض {{ (pagination.current_page - 1) * pagination.per_page + 1 }} -
          {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }} من أصل {{ pagination.total }} مجموعة
        </span>
        <v-pagination
          v-model="pagination.current_page"
          :length="pagination.total_pages"
          :total-visible="7"
          color="#082C69"
          @input="fetchGroups"
        ></v-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { exportToCsv } from "@/utils/csvExport";

export default {
  name: "RouteMakingTripGroups",
  data() {
    return {
      isLoading: false,
      groupsList: [],
      routesList: [],
      operationFilterOptions: [
        { label: "غير معيّن", value: "unassigned" },
      ],
      kpis: {
        total: 0,
        published: 0,
        draft: 0,
        inactive: 0,
      },
      filters: {
        search: "",
        status: "all",
        route_id: null,
        operation_user_id: null,
      },
      statusOptions: [
        { label: "كل الحالات", value: "all" },
        { label: "منشورة", value: "published" },
        { label: "مسودة", value: "draft" },
        { label: "غير نشطة", value: "inactive" },
      ],
      pagination: {
        current_page: 1,
        total_pages: 1,
        per_page: 10,
        total: 0,
      },
      debounceTimeout: null,
    };
  },
  created() {
    this.fetchRoutes();
    this.fetchOperationUsers();
    this.fetchGroups();
  },
  methods: {
    fetchOperationUsers() {
      axios
        .get("/trip-groups/eligible-operation-users")
        .then((res) => {
          // API contract: { success, data: { available, message, users: [...] } }
          const payload = res && res.data ? res.data.data : null;
          const list = Array.isArray(payload)
            ? payload
            : (payload && Array.isArray(payload.users) ? payload.users : []);
          const users = list.map((u) => ({
            label: u.name,
            value: u.id,
          }));
          this.operationFilterOptions = [
            { label: "غير معيّن", value: "unassigned" },
            ...users,
          ];
        })
        .catch((err) => {
          console.error("Error loading operation users filter:", err);
        });
    },
    fetchRoutes() {
      axios
        .get("/routes/all")
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          this.routesList = list.map((r) => ({
            id: r.id,
            name: r.name,
            code: r.route_code || `R-${String(r.id).padStart(4, "0")}`,
            displayName: `${r.route_code || 'R-' + String(r.id).padStart(4, '0')} - ${r.name}`,
          }));
        })
        .catch((err) => {
          console.error("Error loading routes list:", err);
        });
    },
    fetchGroups() {
      this.isLoading = true;
      const params = {
        page: this.pagination.current_page,
        per_page: this.pagination.per_page,
        search: this.filters.search || undefined,
        status: this.filters.status !== "all" ? this.filters.status : undefined,
        route_id: this.filters.route_id || undefined,
        operation_user_id: this.filters.operation_user_id || undefined,
      };

      axios
        .get("/trip-groups", { params })
        .then((res) => {
          if (res.data && res.data.success) {
            const data = res.data.data;
            this.groupsList = data.data || [];
            this.pagination.current_page = data.current_page || 1;
            this.pagination.total_pages = data.last_page || 1;
            this.pagination.total = data.total || 0;

            if (res.data.kpis) {
              this.kpis = res.data.kpis;
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching trip groups:", err);
          this.notifyUser("error", "خطأ", "تعذر تحميل مجموعات الرحلات");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onFilterChange() {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.pagination.current_page = 1;
        this.fetchGroups();
      }, 300);
    },
    changeGroupStatus(group, newStatus) {
      if (newStatus === "published" && group.trips_count === 0) {
        this.notifyUser("warn", "تنبيه", "لا يمكن نشر المجموعة بدون إضافة رحلة واحدة على الأقل");
        return;
      }

      this.isLoading = true;
      axios
        .post(`/trip-groups/${group.id}/change-status`, { status: newStatus })
        .then((res) => {
          if (res.data && res.data.success) {
            this.notifyUser("success", "تم بنجاح", res.data.message || "تم تحديث حالة المجموعة");
            this.fetchGroups();
          } else {
            this.notifyUser("error", "خطأ", res.data?.message || "تعذر تغيير الحالة");
          }
        })
        .catch((err) => {
          const msg = err.response?.data?.message || "حدث خطأ أثناء تغيير الحالة";
          this.notifyUser("error", "فشل التحديث", msg);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    exportGroupsCsv() {
      if (this.groupsList.length === 0) return;

      const headers = [
        "كود المجموعة",
        "اسم المجموعة",
        "الحالة",
        "مسؤول التشغيل",
        "عدد الرحلات",
        "إجمالي المسافة (كم)",
        "ملخص المسارات",
        "تاريخ الإنشاء",
      ];

      const rows = this.groupsList.map((g) => [
        g.group_code,
        g.name,
        g.status_label,
        g.current_operation_user ? g.current_operation_user.name : "غير معيّن",
        g.trips_count,
        g.total_distance_km,
        g.routes_summary ? g.routes_summary.display_text : "—",
        g.created_at || "—",
      ]);

      const metadata = [
        { label: "تقرير", value: "مجموعات رحلات BeepGo - Route Making" },
        { label: "تاريخ الاستخراج", value: new Date().toISOString().slice(0, 10) },
        { label: "إجمالي المجموعات في الملف", value: rows.length },
      ];

      exportToCsv({
        filename: `beepgo-trip-groups-${new Date().toISOString().slice(0, 10)}.csv`,
        headers,
        rows,
        metadata,
      });
    },
    notifyUser(type, title, text) {
      if (this.$swal) {
        this.$swal({
          icon: type,
          title: title,
          text: text,
          timer: 3500,
          showConfirmButton: false,
        });
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap");

.route-making-container {
  font-family: "Cairo", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  direction: rtl;
  text-align: right;
  color: #1E293B;
  padding: 8px 4px 40px 4px;
}

/* Header */
.rm-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.rm-badge-section {
  display: inline-block;
  background-color: rgba(8, 44, 105, 0.08);
  color: #082C69;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 6px;
}

.rm-main-title {
  font-size: 24px;
  font-weight: 800;
  color: #082C69;
  margin: 0;
  line-height: 1.3;
}

.rm-subtitle {
  font-size: 13px;
  color: #64748B;
  margin: 4px 0 0 0;
}

/* Buttons */
.rm-btn-primary {
  background-color: #082C69 !important;
  color: #FFFFFF !important;
  font-family: "Cairo", sans-serif !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  border-radius: 10px !important;
  text-transform: none !important;
  padding: 0 16px !important;
  height: 40px !important;
}

.rm-btn-secondary {
  background-color: #FFFFFF !important;
  color: #082C69 !important;
  border: 1px solid #CBD5E1 !important;
  font-family: "Cairo", sans-serif !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  border-radius: 10px !important;
  text-transform: none !important;
  padding: 0 14px !important;
  height: 40px !important;
}

/* Stat Cards */
.rm-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.rm-stat-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-stat-content {
  display: flex;
  flex-direction: column;
}

.rm-stat-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
  margin-bottom: 2px;
}

.rm-stat-value {
  font-size: 28px;
  font-weight: 800;
  color: #082C69;
  line-height: 1.2;
}

.rm-stat-caption {
  font-size: 11px;
  color: #94A3B8;
  margin-top: 2px;
}

.rm-stat-icon-box {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rm-icon-blue { background: #EEF2F6; }
.rm-icon-green { background: #ECFDF5; }
.rm-icon-amber { background: #FFFBEB; }
.rm-icon-rose { background: #FEF2F2; }

.text-emerald { color: #10B981 !important; }
.text-amber { color: #F59E0B !important; }
.text-rose { color: #EF4444 !important; }
.text-navy { color: #082C69 !important; }

/* Cards Container */
.rm-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #082C69;
  margin: 0;
}

.rm-card-subtitle {
  font-size: 12px;
  color: #64748B;
  margin: 2px 0 0 0;
}

.rm-icon-header-box {
  width: 36px;
  height: 36px;
  background: #EEF2F6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Custom Table */
.table-responsive {
  overflow-x: auto;
}

.rm-custom-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.rm-custom-table th {
  background-color: #F8FAFC;
  color: #475569;
  font-weight: 700;
  padding: 12px 14px;
  text-align: right;
  border-bottom: 2px solid #E2E8F0;
  white-space: nowrap;
}

.rm-custom-table td {
  padding: 14px;
  border-bottom: 1px solid #F1F5F9;
  color: #1E293B;
  vertical-align: middle;
}

.rm-custom-table tbody tr:hover {
  background-color: #F8FAFC;
}

/* Pills & Badges */
.rm-code-pill {
  display: inline-block;
  background: #EEF2F6;
  color: #082C69;
  font-size: 12px;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.5px;
}

.rm-group-name-link {
  color: #082C69;
  font-weight: 700;
  text-decoration: none;
}
.rm-group-name-link:hover {
  text-decoration: underline;
}

.rm-routes-pill {
  display: inline-flex;
  align-items: center;
  background: #E0F2FE;
  color: #0369A1;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 12px;
  cursor: pointer;
}

.rm-unassigned-pill {
  display: inline-block;
  background: #F1F5F9;
  color: #64748B;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
}

.rm-count-badge {
  display: inline-block;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  color: #082C69;
  font-weight: 700;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
}

/* Status Badges */
.rm-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-published {
  background-color: #ECFDF5;
  color: #047857;
}
.status-published .status-dot { background-color: #10B981; }

.status-draft {
  background-color: #FFFBEB;
  color: #B45309;
}
.status-draft .status-dot { background-color: #F59E0B; }

.status-inactive {
  background-color: #F1F5F9;
  color: #64748B;
}
.status-inactive .status-dot { background-color: #94A3B8; }

/* Actions */
.rm-action-btn {
  background: #F8FAFC !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 8px !important;
}

.rm-per-page-select {
  width: 80px;
}

.border-top {
  border-top: 1px solid #E2E8F0;
}
</style>
